import React, { useEffect, useMemo, useRef, useState } from "react";
import janaAvatar from "../../Assets/jana-avatar.jpg";

function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isNearFooter, setIsNearFooter] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I am Janarddan's AI assistant. Ask me anything about his projects, skills, or experience." },
  ]);
  const [input, setInput] = useState("");
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, isOpen]);

  // Add scroll detection for dynamic positioning
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollBottom = scrollTop + windowHeight;
      
      // If user is near the bottom (within 200px), move chatbot up
      if (documentHeight - scrollBottom < 200) {
        setIsNearFooter(true);
      } else {
        setIsNearFooter(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const canSend = useMemo(() => input.trim().length > 0 && !isSending, [input, isSending]);

  async function sendMessage(e) {
    e?.preventDefault?.();
    if (!canSend) return;

    const userMessage = { role: "user", content: input.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsSending(true);

    // Add placeholder assistant message
    const assistantIndex = messages.length + 1;
    setMessages(prev => [...prev, { role: "assistant", content: "", isStreaming: true }]);

    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-stream": "true",
        },
        body: JSON.stringify({ query: userMessage.content }),
      });

      if (!response.body) throw new Error("No response body");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let botMessage = "";
      let buffer = "";
      let wordQueue = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        if (chunk) {
          buffer += chunk;
          
          // Process buffer for word-by-word streaming
          if (buffer.length > 0) {
            // Split by words, punctuation, and special characters
            const words = buffer.split(/(\s+|[.,!?;:()[\]{}"'`~@#$%^&*+=|\\/<>])/);
            
            if (words.length > 1) {
              // We have at least one complete word
              const completePart = words.slice(0, -1).join('');
              buffer = words[words.length - 1]; // Keep incomplete part in buffer
              
              // Split the complete part into individual words and add to queue
              const individualWords = completePart.split(/(\s+)/);
              wordQueue.push(...individualWords);
            }
          }
        }
      }

      // Add any remaining buffer content
      if (buffer.length > 0) {
        const remainingWords = buffer.split(/(\s+)/);
        wordQueue.push(...remainingWords);
      }

      // Process word queue with realistic typing delays
      for (let i = 0; i < wordQueue.length; i++) {
        const word = wordQueue[i];
        botMessage += word;
        
        // Update message immediately for each word
        setMessages(prev => {
          const newMsgs = [...prev];
          if (newMsgs[assistantIndex]) {
            newMsgs[assistantIndex] = { 
              ...newMsgs[assistantIndex], 
              content: botMessage, 
              isStreaming: true 
            };
          }
          return newMsgs;
        });

        // Smooth scroll as it grows
        if (listRef.current) {
          listRef.current.scrollTop = listRef.current.scrollHeight;
        }

        // Calculate realistic typing delay based on content
        let typingDelay = 50; // Base delay - super fast now
        
        // Adjust delay based on content type
        if (word.includes('\n')) {
          // Much longer delay for line breaks
          typingDelay = 100; // Super fast
        } else if (word.includes('.')) {
          // Longer delay for sentence endings
          typingDelay = 90; // Super fast
        } else if (word.includes(',')) {
          // Medium delay for commas
          typingDelay = 75; // Super fast
        } else if (word.includes('!') || word.includes('?')) {
          // Longer delay for exclamations and questions
          typingDelay = 80; // Super fast
        } else if (word.length > 8) {
          // Longer delay for longer words
          typingDelay = 70; // Super fast
        } else if (word.trim() === '') {
          // Shorter delay for spaces
          typingDelay = 40; // Super fast
        }
        
        // Add randomness for natural feel
        typingDelay += Math.random() * 50; // Much smaller randomness
        
        // Wait before processing next word
        await new Promise(resolve => setTimeout(resolve, typingDelay));
      }

      // Final update with complete message
      setMessages(prev => {
        const newMsgs = [...prev];
        if (newMsgs[assistantIndex]) {
          newMsgs[assistantIndex] = { 
            ...newMsgs[assistantIndex], 
            content: botMessage, 
            isStreaming: false 
          };
        }
        return newMsgs;
      });

    } catch (err) {
      setMessages(prev => [
        ...prev.slice(0, -1),
        { role: "assistant", content: "⚠️ Sorry, something went wrong." },
      ]);
    } finally {
      setIsSending(false);
    }
  }

  const toggleChatbot = () => {
    setIsOpen(!isOpen);
  };

  const closeChatbot = () => {
    setIsOpen(false);
  };

  return (
    <>
      {/* Chatbot Toggle Button */}
      <button
        onClick={toggleChatbot}
        className="chatbot-toggle"
        style={{
          position: "fixed",
          bottom: isNearFooter ? "80px" : "20px",
          right: "20px",
          zIndex: 1000,
          minWidth: "280px",
          height: "70px",
          borderRadius: "35px",
          backgroundColor: "#623686",
          border: "none",
          color: "white",
          fontSize: "14px",
          cursor: "pointer",
          boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
          transition: "all 0.3s ease",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          padding: "0 20px",
        }}
        title="Chat with Janarddan's AI Assistant"
      >
        <img
          src={janaAvatar}
          alt="Janarddan's AI"
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            objectFit: "cover",
            border: "2px solid white",
            flexShrink: 0,
          }}
        />
        <div style={{ 
          display: "flex", 
          flexDirection: "column", 
          alignItems: "flex-start",
          lineHeight: "1.2",
        }}>
          <span style={{ fontWeight: "600", fontSize: "13px" }}>
            Wanna chat with
          </span>
          <span style={{ fontWeight: "500", fontSize: "12px", opacity: 0.9 }}>
            Janarddan's AI Assistant?
          </span>
        </div>
      </button>

      {/* Chatbot Interface */}
      {isOpen && (
        <div
          className="chatbot-container"
          style={{
            position: "fixed",
            bottom: isNearFooter ? "80px" : "20px",
            right: "20px",
            zIndex: 1001,
            width: "350px",
            height: "500px",
            backgroundColor: "white",
            borderRadius: "15px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            border: "2px solid #623686",
          }}
        >
          {/* Header */}
          <div
            style={{
              backgroundColor: "#623686",
              color: "white",
              padding: "15px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderTopLeftRadius: "13px",
              borderTopRightRadius: "13px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <img
                src={janaAvatar}
                alt="Janarddan's AI"
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
              <div>
                <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                  Janarddan's AI Assistant
                </div>
                <div style={{ fontSize: "11px", opacity: 0.8 }}>
                  Ask me anything about projects, skills, or experience
                </div>
              </div>
            </div>
            <button
              onClick={closeChatbot}
              style={{
                background: "none",
                border: "none",
                color: "white",
                fontSize: "20px",
                cursor: "pointer",
                padding: "0",
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </button>
          </div>

          {/* Messages */}
          <div
            ref={listRef}
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "15px",
              display: "flex",
              flexDirection: "column",
              gap: "15px",
            }}
          >
            {messages.map((message, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  justifyContent: message.role === "user" ? "flex-end" : "flex-start",
                  marginBottom: "10px",
                }}
              >
                <div
                  style={{
                    maxWidth: "80%",
                    padding: "12px 16px",
                    borderRadius: "18px",
                    backgroundColor: message.role === "user" ? "#623686" : "#f0f0f0",
                    color: message.role === "user" ? "white" : "#333",
                    fontSize: "14px",
                    lineHeight: "1.4",
                    wordWrap: "break-word",
                    position: "relative",
                  }}
                >
                  {message.content}
                  {message.isStreaming && (
                    <span
                      style={{
                        display: "inline-block",
                        width: "8px",
                        height: "8px",
                        backgroundColor: "#623686",
                        borderRadius: "50%",
                        marginLeft: "8px",
                        animation: "pulse 1.5s infinite",
                      }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <div
            style={{
              padding: "15px",
              borderTop: "1px solid #eee",
              backgroundColor: "#fafafa",
            }}
          >
            <form onSubmit={sendMessage} style={{ display: "flex", gap: "10px" }}>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask me anything..."
                disabled={isSending}
                style={{
                  flex: 1,
                  padding: "12px 16px",
                  border: "2px solid #ddd",
                  borderRadius: "25px",
                  fontSize: "14px",
                  outline: "none",
                  transition: "border-color 0.3s ease",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#623686";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#ddd";
                }}
              />
              <button
                type="submit"
                disabled={!canSend}
                style={{
                  padding: "12px 20px",
                  backgroundColor: canSend ? "#623686" : "#ccc",
                  color: "white",
                  border: "none",
                  borderRadius: "25px",
                  fontSize: "14px",
                  cursor: canSend ? "pointer" : "not-allowed",
                  transition: "background-color 0.3s ease",
                }}
              >
                {isSending ? "..." : "Send"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* CSS for pulse animation */}
      <style jsx>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </>
  );
}

export default Chatbot;


